<template>
    <div>
        <div v-if="!bloodResultId">
            <b-datatable
                v-if="listRoute && !bloodResultId"
                :key="datatableKey"
                ref="dataTable"
                :columns="columns"
                :endpoint="listRoute"
                :hidden-columns="hiddenColumns"
                :initial-filters="initialFilters"
                :grid-title="t('patients::grid.title.blood_results')"
                has-filter-selector
                hide-buttons
                qc-mode
                item-key="id"
            >
                <template #buttons>
                    <div class="px-2">
                        <v-tooltip top transition="v-fab-transition">
                            <template #activator="{ on, attrs }">
                                <v-btn
                                    v-if="can('api.v1.patients.blood-results.store')"
                                    color="primary"
                                    icon
                                    v-bind="attrs"
                                    :href="createItemUrl()"
                                    v-on="on"
                                >
                                    <v-icon>mdi-plus-circle</v-icon>
                                </v-btn>
                            </template>
                            <span> {{ t('patients::button.create.blood_result') }}</span>
                        </v-tooltip>
                    </div>
                </template>
                <template #filters="{ filters }">
                    <v-col cols="4">
                        <b-filter-date
                            v-model="filters.filters.filter_date_taken"
                            range
                            :label="t('patients::grid.label.date_taken')"
                            @clear="onCloseDateFilter(filters, 'filter_date_taken')"
                        ></b-filter-date>
                    </v-col>
                    <v-col cols="4">
                        <b-filter-select
                            v-model="filters.filters.provider_id"
                            :label="t('patients::options.label.laboratory_cc_device')"
                            :items="labCCAstmOptions"
                            :clearable="filters.filters.provider_id !== null"
                            multiple
                        ></b-filter-select>
                    </v-col>
                    <v-col cols="4">
                        <b-filter-select
                            v-model="filters.filters.color_result"
                            :label="t('patients::options.label.blood_result_colors')"
                            :items="filtersOptions.bloodResultColors"
                            :clearable="filters.filters.color_result !== null"
                        ></b-filter-select>
                    </v-col>
                    <v-col cols="4">
                        <b-filter-select
                            v-model="filters.filters.operator"
                            :label="t('patients::options.label.operator')"
                            :items="operatorsOptions"
                            :clearable="filters.filters.operator !== null"
                        ></b-filter-select>
                    </v-col>
                    <v-col cols="4">
                        <b-filter-select
                            v-model="filters.filters.dispensed"
                            :label="t('patients::form.label.dispensed')"
                            :items="filtersOptions.dispensedOptions"
                            :clearable="filters.filters.dispensed !== null"
                        ></b-filter-select>
                    </v-col>
                </template>

                <template #[`item.color_result`]="{ item }">
                    <div :class="`blood-status ${item.color_result}--text ${item.is_abnormal ? 'abnormal' : ''}--text`">
                        <div :class="`status ${item.color_result}--text ${item.is_abnormal ? 'abnormal' : ''}--text`">
                            <v-icon>icon icon-blood-result</v-icon>
                        </div>
                        <span v-text="item.is_abnormal ? 'Repeat' : item.color_result"></span>
                    </div>
                </template>

                <template #[`item.health_status`]="{ item }">
                    <div v-if="healthStatus(item)" class="grid-status">
                        <span>
                            {{ healthStatus(item) }}
                        </span>
                    </div>
                </template>

                <template #[`item.date_taken`]="{ item }">
                    {{ item.date_taken ? moment(item.date_taken).format($root.dateFormat) : t('default::label.na') }}
                </template>

                <template #[`item.date_received`]="{ item }">
                    {{
                        item.date_received
                            ? moment(item.date_received).format($root.dateFormat)
                            : t('default::label.na')
                    }}
                </template>

                <template #[`item.operator_name`]="{ item }">
                    {{ item.operator_name || t('default::label.na') }}
                </template>

                <template #[`item.rejected`]="{ item }">
                    <div>
                        <v-icon color="red">mdi-alert-octagon-outline</v-icon>
                        <span v-if="item.is_abnormal && item.rejected" class="error--text">{{
                            t('patients::status-blood-result-abnormal')
                        }}</span>
                        <span v-else-if="item.rejected" class="error--text">{{
                            t('patients::status-blood-result-rejected')
                        }}</span>
                    </div>
                </template>
                <template #[`item.actions`]="{ item }">
                    <div class="table-actions">
                        <b-confirm
                            v-if="can('api.v1.patients.blood-results.reject') && item.editable"
                            :title="t('patients::confirmation.reject.title')"
                            :text="t('patients::confirmation.reject.text')"
                            @true="rejectBloodResult(item)"
                        >
                            <template #button="{ on }">
                                <v-btn color="accent" small rounded text class="mr-2" v-on="on">{{
                                    t('patients::button.reject')
                                }}</v-btn>
                            </template>
                        </b-confirm>

                        <v-tooltip v-if="can('blood-results.export-record-as-pdf')" top transition="v-fab-transition">
                            <template #activator="{ on, attrs }">
                                <v-btn
                                    color="disabled-color"
                                    icon
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="exportRecordAsPdf(item)"
                                >
                                    <v-icon size="24">mdi-file-export-outline</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ t('default::button.export_record_as_pdf') }}</span>
                        </v-tooltip>

                        <v-tooltip
                            v-if="can('api.v1.patients.dispensing-days.dispensed') && item.dispensable"
                            top
                            transition="v-fab-transition"
                        >
                            <template #activator="{ on, attrs }">
                                <v-btn
                                    color="disabled-color"
                                    icon
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="openDialog(item, 'dispensing_days')"
                                >
                                    <v-icon>mdi-clipboard-plus-outline</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ t('patients::buttons.dispensing_days') }}</span>
                        </v-tooltip>

                        <v-tooltip v-if="canStoreAE(item)" top transition="v-fab-transition">
                            <template #activator="{ on, attrs }">
                                <v-btn
                                    color="disabled-color"
                                    icon
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="createAdverseEventUrl(item)"
                                >
                                    <v-icon>mdi-text-box-plus-outline</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ t('patients::button.create_adverse_event') }}</span>
                        </v-tooltip>
                        <v-tooltip
                            v-if="item.adverse_event_id && can('api.v1.patients.adverse-events.show')"
                            top
                            transition="v-fab-transition"
                        >
                            <template #activator="{ on, attrs }">
                                <v-btn
                                    color="disabled-color"
                                    icon
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="goToAdverseEventUrl(item)"
                                >
                                    <v-icon>mdi-text-box-search-outline</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ t('patients::button.view_adverse_event') }}</span>
                        </v-tooltip>
                        <v-tooltip
                            v-if="can('api.v1.patients.blood-results.update') && item.editable"
                            top
                            transition="v-fab-transition"
                        >
                            <template #activator="{ on, attrs }">
                                <v-btn color="disabled-color" icon v-bind="attrs" v-on="on" @click="editItemUrl(item)">
                                    <v-icon>mdi-pencil</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ t('patients::button.edit') }}</span>
                        </v-tooltip>
                        <v-tooltip
                            v-if="
                                (can('api.v1.patients.blood-results.show') && !item.editable) ||
                                (can('api.v1.patients.blood-results.show') &&
                                    !can('api.v1.patients.blood-results.update'))
                            "
                            top
                            transition="v-fab-transition"
                        >
                            <template #activator="{ on, attrs }">
                                <v-btn color="disabled-color" icon v-bind="attrs" v-on="on" @click="editItemUrl(item)">
                                    <v-icon>mdi-eye</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ t('patients::button.details') }}</span>
                        </v-tooltip>
                        <b-delete
                            v-if="can('api.v1.patients.blood-results.destroy')"
                            :path="deleteItemUrl(item)"
                            :text="t('default::confirmation.deletion')"
                            :title="t('default::confirmation.general')"
                            icon="mdi-trash-can"
                            @deleted="triggerDeleted"
                        >
                        </b-delete>
                    </div>
                </template>
            </b-datatable>
        </div>
        <blood-results-form v-if="bloodResultId"></blood-results-form>
        <v-dialog
            v-if="selectedBloodResult"
            ref="dialog"
            v-model="dispensingDialog"
            max-width="500px"
            @click:outside="close"
        >
            <v-card>
                <v-card-title class="headline" primary-title>
                    {{ t('patients::dispensing.form.title.add_number_of_days') }}
                </v-card-title>
                <v-card-text>
                    <v-form readonly disabled class="py-5">
                        <v-row>
                            <v-col v-if="selectedBloodResult.color_result" cols="12" lg="12">
                                <label class="pb-2 pl-1" v-text="t('patients::grid.label.result')"></label>
                                <div :class="`blood-status ${selectedBloodResult.color_result}--text`">
                                    <div :class="`status ${selectedBloodResult.color_result}--text`">
                                        <v-icon>icon icon-blood-result</v-icon>
                                    </div>
                                    <span v-text="selectedBloodResult.color_result"></span>
                                </div>
                            </v-col>
                            <v-col v-if="selectedBloodResult.next_due_date" cols="12" lg="6" class="pb-0">
                                <v-text-field
                                    :value="
                                        selectedBloodResult.next_due_date
                                            ? moment(selectedBloodResult.next_due_date).format($root.dateFormat)
                                            : t('default::label.na')
                                    "
                                    rounded
                                    outlined
                                    dense
                                    :label="t('patients::form.label.next_due_date')"
                                ></v-text-field>
                            </v-col>
                            <v-col v-if="selectedBloodResult.validity_date" cols="12" lg="6" class="pb-0">
                                <v-text-field
                                    :value="
                                        selectedBloodResult.validity_date
                                            ? moment(selectedBloodResult.validity_date).format($root.dateFormat)
                                            : t('default::label.na')
                                    "
                                    rounded
                                    outlined
                                    dense
                                    :label="t('patients::form.label.validity_date')"
                                ></v-text-field>
                            </v-col>

                            <v-col v-if="selectedBloodResult.dispensing_days_suggested" cols="12" lg="6" class="pb-0">
                                <v-text-field
                                    v-model="selectedBloodResult.dispensing_days_suggested"
                                    rounded
                                    outlined
                                    dense
                                    :label="t('patients::form.label.dispensing_days_suggested')"
                                ></v-text-field>
                            </v-col>

                            <v-col v-if="selectedBloodResult.dispensing_days_accumulated" cols="12" lg="6" class="pb-0">
                                <v-text-field
                                    v-model="selectedBloodResult.dispensing_days_accumulated"
                                    rounded
                                    outlined
                                    dense
                                    :label="t('patients::form.label.dispensing_days_accumulated')"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-form>
                    <v-row>
                        <v-col cols="12" class="pb-0">
                            <b-input
                                to-mutate="patients::dispensingForm.max_allowed_dispensing_days"
                                :label="t('patients::dispensing.form.label.max_allowed_dispensing_days')"
                                type="number"
                                :is-number="true"
                                :hint="' '"
                                class="pills large w-100 mw-100 pt-2"
                            ></b-input>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="primary" text rounded @click="dispensingDialog = false">{{
                        t('patients::button.cancel')
                    }}</v-btn>
                    <v-spacer></v-spacer>
                    <b-confirm
                        :text="`${maxDaysToDispense} ${t('patients::dispensing.dispense.title.text')} `"
                        :title="t('patients::dispensing.dispense.title')"
                        @true="dispensDay"
                    >
                        <template #button="{ on }">
                            <v-btn color="primary" rounded :disabled="!canSendRequest" v-on="on">
                                {{ t('patients::button.dispense') }}
                            </v-btn>
                        </template>
                    </b-confirm>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
const DispenseDaysMixin = require(process.env.MIX_APP_ROOT +
    '/modules/patients/resources/js/mixins/DispenseDaysMixin.js').default;
export default {
    mixins: [DispenseDaysMixin],
    props: {},
    data() {
        return {
            datatableKey: 0,
            listRoute: null,
            downloadingFile: false,
            hiddenColumns: ['id', 'is_abnormal'],
            initialFilters: {
                operator: null,
                dispensable: null,
            },
            operatorsOptions: [],
            filtersOptions: {
                dispensedOptions: [
                    {
                        text: t('patients::options.dispensed.all'),
                        value: null,
                    },
                    {
                        text: t('patients::options.dispensed.yes'),
                        value: 1,
                    },
                    {
                        text: t('patients::options.dispensed.no'),
                        value: 0,
                    },
                ],
                bloodResultColors: [
                    {
                        text: t('patients::options.blood_results_color.all'),
                        value: null,
                    },
                    {
                        text: t('patients::options.blood_results_color.red'),
                        value: 'red',
                    },
                    {
                        text: t('patients::options.blood_results_color.amber'),
                        value: 'amber',
                    },
                    {
                        text: t('patients::options.blood_results_color.green'),
                        value: 'green',
                    },
                ],
            },
            columns: [
                {
                    text: t('default::grid.label.id'),
                    value: 'id',
                    sortable: false,
                    width: 170,
                },
                {
                    text: t('patients::grid.label.blood_result'),
                    value: 'color_result',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('patients::grid.label.platelets'),
                    value: 'platelets',
                    width: 140,
                    sortable: true,
                },
                {
                    text: t('patients::grid.label.white_blood_count'),
                    value: 'white_blood_count',
                    width: 140,
                    sortable: true,
                },
                {
                    text: t('patients::grid.label.neutrophils'),
                    value: 'neutrophils',
                    width: 140,
                    sortable: true,
                },
                {
                    text: t('patients::grid.label.entered_by'),
                    value: 'entered_by',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('patients::grid.label.date_taken'),
                    value: 'date_taken',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('patients::grid.label.date_received'),
                    value: 'date_received',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('patients::grid.label.blood_result_provider'),
                    value: 'provider_name',
                    width: 180,
                    sortable: true,
                },
                {
                    text: t('patients::grid.label.operator_name'),
                    value: 'operator_name',
                    width: 200,
                    sortable: true,
                },
                {
                    text: null,
                    value: 'rejected',
                    sortable: false,
                    class: 'is_patient_mode',
                    cellClass: 'is_patient_mode',
                    width: 0,
                },
                {
                    text: null,
                    value: 'is_abnormal',
                    sortable: false,
                    width: 0,
                },
                {
                    text: t('default::grid.label.actions'),
                    value: 'actions',
                    sortable: false,
                    fields: 'editable,adverse_event_id,dispensable,next_due_date,validity_date,dispensing_days_suggested,dispensing_days_accumulated,patient.id',
                    align: 'end',
                    width: 300,
                },
            ],
            labCCAstmOptions: [],
        };
    },
    computed: {
        tab() {
            return this.$store.state.patients.tab;
        },
        patient() {
            return this.$store.state.patients.patient;
        },
        patientId() {
            return this.$store.state.patients.patient.id;
        },
        bloodResultId() {
            return this.$store.state.patients.bloodResult.id;
        },
    },
    watch: {
        tab(val) {
            if (val === 4) {
                this.$refs.dataTable.resetTable();
            }
        },
    },
    mounted() {
        this.listRoute = route('api.v1.patients.blood-results.index', {
            patient: this.patientId,
        });
        this.loadLaboratoryCCASTMDevicesOptions();
        this.loadOperatorsOptions();
    },
    methods: {
        loadOperatorsOptions() {
            axios
                .get(
                    route('api.v1.operators.options', {
                        patient_id: this.patientId,
                        allowed_operators: 'patient',
                    })
                )
                .then((response) => {
                    if (!response) {
                        return;
                    }
                    this.operatorsOptions = _.union(
                        [
                            {
                                text: t('patients::status.all'),
                                value: null,
                            },
                        ],
                        response.data.data && response.data.data.length
                            ? response.data.data.map((x) => ({ value: x.operator.id, text: x.operator.name }))
                            : []
                    );
                });
        },
        async exportRecordAsPdf(item) {
            if (this.downloadingFile) {
                return;
            }

            this.downloadingFile = true;

            return await axios
                .get(route('blood-results.export-record-as-pdf', { blood_result: item.id }))
                .then((response) => {
                    const link = document.createElement('a');
                    link.href = response.request.responseURL;
                    document.body.appendChild(link);
                    link.click();
                })
                .catch((error) => {
                    if (!error || !error.response) {
                        return;
                    }
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => {
                    _.delay(() => {
                        this.downloadingFile = false;
                    }, 2000);
                });
        },
        goToAdverseEventUrl(item) {
            this.$store.dispatch('patients/changeTab', 6);
            this.$store.dispatch('adverse_event/loadAdverseEvent', {
                patient_id: this.patientId,
                id: item.adverse_event_id,
            });
        },
        canStoreAE(item) {
            // if (this.patient.diagnosis.is_under_monitoring) {
            //     return true;
            // }
            const allowColors = ['red', 'amber', 'green'];
            if (item.rejected) {
                return false;
            }
            if (!allowColors.includes(item.color_result)) {
                return false;
            }
            return this.can('api.v1.patients.adverse-events.store') && !item.adverse_event_id;
        },
        createAdverseEventUrl(item) {
            this.$store.dispatch('patients/changeTab', 6);
            this.$store.dispatch('adverse_event/newAdverseEvent', { id: item.id });
        },
        rejectBloodResult(item) {
            return axios
                .put(
                    route('api.v1.patients.blood-results.reject', {
                        patient: this.patientId,
                        blood_result: item.id,
                    })
                )
                .then(() => {
                    this.$root.notify(
                        'info',
                        t('patients::alert.success.reject.title'),
                        t('patients::alert.success.reject.text')
                    );
                    this.datatableKey++;
                    this.$store.dispatch('patients/reloadPatientData');
                })
                .catch(() => {
                    this.$root.notify(
                        'error',
                        t('patients::alert.error.reject.title'),
                        t('patients::alert.error.reject.text')
                    );
                });
        },
        onCloseDateFilter(filters, filter) {
            switch (filter) {
                case 'filter_date_taken':
                    filters.filters.date_taken = null;
                    filters.filters.filter_date_taken = null;
                    break;
                default:
                    break;
            }
        },
        loadLaboratoryCCASTMDevicesOptions() {
            axios
                .get(
                    route('api.v1.providers.options', {
                        sort: 'name',
                        patient_id: this.patientId,
                        allowed_providers: 'patient',
                    })
                )
                .then((response) => {
                    if (!response) {
                        return;
                    }
                    this.labCCAstmOptions = _.union(
                        [
                            {
                                text: t('patients::status.all'),
                                value: '',
                            },
                        ],
                        response.data.data && response.data.data.length
                            ? response.data.data.map((x) => ({ value: x.id, text: x.name }))
                            : []
                    );
                });
        },
        deleteItemUrl(item) {
            return route('api.v1.patients.blood-results.destroy', { patient: this.patientId, blood_result: item.id });
        },
        triggerDeleted() {
            this.datatableKey++;
            this.$root.notify('info', t('default::notification.deleted'));
        },
        editItemUrl(item) {
            this.$store.dispatch('patients/resetErrorMessages');
            axios
                .get(
                    route('api.v1.patients.blood-results.show', {
                        patient: this.patientId,
                        blood_result: item.id,
                        fields: '*,blood_result_provider_id,operator,editable,rejected,blood_result_provider.id,blood_result_provider.name,blood_result_provider.visible_in_qc_list,provider_type',
                    })
                )
                .then((response) => {
                    if (!response) {
                        return;
                    }
                    const data = response.data.data;
                    this.$store.dispatch('patients/loadBloodResult', data);
                });
        },
        createItemUrl() {
            this.$store.dispatch('patients/newBloodResult');
        },
        healthStatus(item) {
            if (item && item.health_status) {
                return _.map(item.health_status, (status) => {
                    return t('patients::health_status.' + status);
                }).join(', ');
            }
            return false;
        },
    },
};
</script>

<style>
.v-application .green--text.abnormal--text {
    color: #75797c !important;
    caret-color: #75797c !important;
}
</style>
